import { useFormContext } from 'react-hook-form';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { AnalyticSearchRequestDto } from 'src/generated/services/TFinancialApi';
import { useTourContext } from 'src/presentations/guided-tours';
import IconArrow from '../../../../../assets/images/icons/arrow-left_ico.svg';
import IconImport from '../../../../../assets/images/icons/import-primary.svg';
import Button from '../../../atom/button';
import { useFilters } from '../useAnalytics';
import { filterAccordionsMapper } from '../analyticsMapper';
import { SaveReportModal } from './SaveReportModal';
import { ChartViewType } from '../sidebar/types';
import { FilterHeader } from '../../filter/FilterHeader';
import { RhfFieldInfo } from './RhfFieldInfo';
import { FilterTriggerButton, FilterDrawerContent } from './FilterDrawer';
import { useAnalyticsData } from '../context/AnalyticsDataContext';

interface IAnalyticsHeaderProps {
  selectedChartView?: ChartViewType;
  setDefaultFilters?: (object) => void;
  onScreenShot?: () => void;
  allowedCurrencies?: string[];
  isLoading?: boolean;
  sanitizedFormValues?: AnalyticSearchRequestDto;
}

export const AnalyticsHeader = ({
  selectedChartView,
  setDefaultFilters,
  onScreenShot,
  allowedCurrencies,
  isLoading,
  sanitizedFormValues,
}: IAnalyticsHeaderProps) => {
  const { formState } = useFormContext();
  const [isShowModal, setIsShowModal] = useState(false);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isFilterChanged, setIsFilterChanged] = useState(false);
  const navigate = useNavigate();
  const tourChart = useTourContext().appTour.registerTour('reports-analytics-chart');
  const tourTable = useTourContext().appTour.registerTour('reports-analytics-table');
  // Get table filter count to track if table filters are applied
  const { activeFilterCount, activeTab } = useAnalyticsData();

  // Only call useFilters when drawer is open and we have sanitizedFormValues
  const { data: chartFilters, isLoading: isLoadingFilters } = useFilters('REPORT', sanitizedFormValues, {
    enabled: isFilterDrawerOpen && !!sanitizedFormValues,
  });

  // Generate filter accordions from the fetched data
  const filterAccordions = useMemo(() => (chartFilters?.filters ? filterAccordionsMapper(chartFilters.filters) : []), [chartFilters?.filters]);

  return (
    <>
      {isLoading ? (
        <div className='flex items-center justify-center'>
          <CircularProgress color='primary' />
        </div>
      ) : (
        <div className='sticky top-0 flex items-center gap-14 xl:gap-20 px-16 xl:px-24 pt-20 xl:pt-24 pb-16 xl:pb-20 bg-white border-b border-neutral-20 z-10'>
          <div className='flex-1 flex items-center gap-18'>
            <button
              onClick={() => {
                // Get the stored section from localStorage or default to ANALYTIC
                const sectionName = localStorage.getItem('lastSectionName') || 'ANALYTIC';
                navigate(`/?section=${sectionName}`);
              }}
              className='w-40 h-40 rounded-full bg-neutral-10 border border-neutral-20 flex items-center justify-center cursor-pointer'
            >
              <img src={IconArrow} alt='Back' />
            </button>
            <div id={tourChart.registerStep('reportsAnalyticsPage')} className='flex flex-col gap-4'>
              <RhfFieldInfo fieldName='name' defaultValue='New Report' infoClassname='text-20 xl:text-24 font-bold text-blue_grey-80' />

              <RhfFieldInfo
                fieldName='description'
                defaultValue='Report Description'
                textSize='md'
                infoClassname='text-12 xl:text-14 font-medium text-neutral-60'
              />
            </div>
          </div>

          <div className='ml-auto flex items-center gap-12 xl:gap-16'>
            <div id={activeTab === 'table' ? tourTable.registerStep('saveExportButtons') : undefined}>
              <Button
                variant='primary'
                size='medium'
                className='!min-w-0 !h-36'
                disabled={!formState?.isDirty && !isFilterChanged && activeFilterCount === 0}
                onClick={() => setIsShowModal(true)}
              >
                Save Report
              </Button>
            </div>
            <button
              aria-label='import'
              type='button'
              className='w-36 h-36 flex items-center justify-center border border-neutral-20 rounded-6'
              onClick={onScreenShot}
            >
              <img src={IconImport} alt='' />
            </button>
            <div className='w-1 h-16 bg-neutral-20' />
            <FilterHeader onChangeFilter={() => {}} allowedCurrencies={allowedCurrencies} />
            <div id={tourChart.registerStep('filterPanel')}>
              <FilterTriggerButton onClick={() => setIsFilterDrawerOpen(true)} />
            </div>
          </div>
          <SaveReportModal
            open={isShowModal}
            title='Save Report'
            onClose={() => {
              setIsShowModal(false);
            }}
            setDefaultFilters={setDefaultFilters}
            selectedChartView={selectedChartView}
          />
        </div>
      )}

      <FilterDrawerContent
        filterAccordions={filterAccordions}
        onChangeFilterNew={() => {
          setIsFilterChanged(true);
          setIsFilterDrawerOpen(false);
        }}
        isOpen={isFilterDrawerOpen}
        setIsOpen={setIsFilterDrawerOpen}
        isLoading={isLoadingFilters}
      />
    </>
  );
};
